import React, { useState, useEffect } from "react";
import { apiGet } from "../../services/api";
import "./ReferenceStats.css";

interface MonthlyData {
  month: string;
  [key: string]: string | number;
}

interface ReferenceStatsData {
  reference_sources?: string[];
  totals?: Record<string, number>;
  monthly_data?: MonthlyData[];
}

const sourceColors: Record<string, string> = {
  "Board_Walk-in": "#3b82f6",
  "Old_Patient": "#22c55e",
  "Referred_by_Old_Patient": "#f59e0b",
  "Doctors_Referral": "#8b5cf6",
  "Online_Media": "#ec4899"
};

const sourceLabels: Record<string, string> = {
  "Board_Walk-in": "Walk-in",
  "Old_Patient": "Old Patient",
  "Referred_by_Old_Patient": "Patient Referral",
  "Doctors_Referral": "Doctor Referral",
  "Online_Media": "Online/Media"
};

const ReferenceStats = () => {
  const [data, setData] = useState<ReferenceStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/api/admin/reference-stats?year=${year}`) as ReferenceStatsData;
      setData(result);
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 502 || e.status === 503 || e.status === 504) {
        setError("Backend unavailable. Run: docker compose logs backend");
      } else {
        setError("Failed to load reference statistics");
      }
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [year, loadData]);

  const getMaxValue = () => {
    if (!data?.monthly_data) return 0;
    let max = 0;
    data.monthly_data.forEach(month => {
      Object.keys(sourceColors).forEach(key => {
        const val = Number(month[key]) || 0;
        if (val > max) max = val;
      });
    });
    return max || 1;
  };

  const maxValue = getMaxValue();

  if (loading) {
    return (
      <div className="reference-stats-container">
        <div className="loading-state">Loading reference statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reference-stats-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="mod-standalone-window">
      <div className="mod-window-header reference-stats-header">
        <div>
          <h3 className="reference-stats-title">
              📈 Patient Acquisition Sources
          </h3>
          <p className="mod-text-muted">Track where your walk-ins and referrals are coming from.</p>
        </div>
        
        <div className="year-selector ref-year-selector">
          <button
            type="button"
            onClick={() => setYear(y => y - 1)}
            className="year-btn ref-year-btn"
          >
            ◀
          </button>
          <span className="ref-year-value">{year}</span>
          <button
            type="button"
            onClick={() => setYear(y => y + 1)}
            className="year-btn ref-year-btn"
            disabled={year >= new Date().getFullYear()}
          >
            ▶
          </button>
        </div>
      </div>

      <div className="reference-summary-cards">
        {data?.reference_sources?.map((source, idx) => {
          const count = data.totals?.[source] || 0;
          const key = source.replace("/", "_").replace(/ /g, "_").replace("'", "");
          const color = sourceColors[key] || sourceColors[source] || "#64748b";
          const label = sourceLabels[source] || sourceLabels[key] || source;

          return (
            <div
              key={idx}
              className="ref-summary-card"
              style={{ "--ref-accent": color } as React.CSSProperties}
            >
              <div className="ref-card-count">{count}</div>
              <div className="ref-card-label">{label}</div>
            </div>
          );
        })}
      </div>

      <div className="reference-chart-section">
        <h4>Monthly Breakdown</h4>
        <div className="reference-bar-chart">
          {data?.monthly_data?.map((monthData, idx) => (
            <div key={idx} className="month-column">
              <div className="bars-container">
                {Object.keys(sourceColors).map(key => {
                  const value = Number(monthData[key]) || 0;
                  const height = maxValue > 0 ? (value / maxValue) * 100 : 0;

                  return (
                    <div
                      key={key}
                      className="ref-bar"
                      style={{
                        "--bar-height": `${height}%`,
                        "--bar-color": sourceColors[key],
                        "--bar-min": value > 0 ? "4px" : "0px",
                        "--bar-shadow": value > 0 ? "0 4px 6px rgba(0,0,0,0.1)" : "none"
                      } as React.CSSProperties}
                      title={`${sourceLabels[key]}: ${value} patients`}
                    >
                      {value > 0 && height > 15 && (
                        <span className="bar-value">{value}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="month-label">{monthData.month}</div>
            </div>
          ))}
        </div>

        <div className="reference-legend">
          {Object.entries(sourceLabels).map(([key, label]) => (
            <div key={key} className="legend-item">
              <span
                className="legend-color"
                style={{ "--legend-color": sourceColors[key] } as React.CSSProperties}
              />
              <span className="legend-text">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReferenceStats;
